import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import { emitLinkOutcome } from './recurringAnalytics'
import type { LocalDate } from './recurringRules'
import { toFiledPayment, type Bill, type Payment } from './recurringWire'
import { BUSY_COPY } from './useLifecycle'

/** The sentences a link failure shows, in place, in the picker. */
export const LINK_COPY = {
  /** Review Focus 5: the picker adds an Open link to the owning bill. */
  owned: 'That transaction already belongs to another bill.',
  filled: 'This period already has a payment.',
  /** NEW COPY (owner review): the transaction is already on THIS bill, under another period. */
  same: 'That transaction is already linked to this bill.',
  /** NEW COPY (owner review): the iOS "Couldn't … it. Try again." family. */
  failed: "Couldn't link it. Try again.",
} as const
/** NEW COPY (owner review), the same family. */
export const UNLINK_FAILED = "Couldn't unlink it. Try again."
/** Task 10: a 422 from match-history — no account, an unknown frequency, or no start date. */
export const FIND_UNAVAILABLE = "Pick the account it's paid from to find past payments."
/** iOS `matchHistoryFailureMessage`'s fallback. */
export const FIND_FAILED = "Couldn't look for past payments. Try again."

/** "Linked N past payments" / "No past payments found" (iOS, pluralised as iOS does). */
export function findPastCopy(linked: number): string {
  if (linked === 0) return 'No past payments found'
  return `Linked ${linked} past payment${linked === 1 ? '' : 's'}`
}

/**
 * Why a link, unlink or search did not happen. `message` is the sentence to
 * show as it stands; `recurringId` names the bill that owns the transaction
 * (`owned` only).
 *
 * - `owned` — 409 with `detail.recurring_id`: another live stream has it.
 *   `recurringId` is null when the backend could not name the owner.
 * - `filled` — 409: this period already holds a payment (one per period).
 * - `same` — 409: the transaction is already linked to this bill.
 * - `unavailable` — 422 from match-history: nothing to match against.
 * - `busy` — another link, unlink or search on this bill is out; nothing sent.
 * - `failed` — anything else, including an unrecognised 409. Never guessed
 *   into one of the above: a wrong "already has a payment" is worse than a
 *   plain "try again".
 */
export class PeriodLinkError extends Error {
  readonly kind: 'owned' | 'filled' | 'same' | 'unavailable' | 'busy' | 'failed'
  readonly recurringId: string | null
  constructor(kind: PeriodLinkError['kind'], message: string, recurringId: string | null = null, cause?: unknown) {
    super(message, { cause })
    this.name = 'PeriodLinkError'
    this.kind = kind
    this.recurringId = recurringId
  }
}

/** The payment a link filed, and the period the server filed it under. */
export type Filed = { dueDate: string | null; payment: Payment }

type Op =
  | { op: 'link'; transactionId: string; dueDate: LocalDate }
  | { op: 'unlink'; paymentId: string; transactionId: string | null }
  | { op: 'find' }

type Outcome = { op: 'link'; filed: Filed } | { op: 'unlink' } | { op: 'find'; linked: number }

/** FastAPI's `detail`, as a string or as the ownership 409's `{ message, recurring_id }`. */
function detailOf(error: unknown): { text: string; owner: string | null } {
  const detail = typeof error === 'object' && error !== null ? (error as { detail?: unknown }).detail : undefined
  if (typeof detail === 'string') return { text: detail, owner: null }
  if (typeof detail === 'object' && detail !== null) {
    const { message, recurring_id } = detail as { message?: unknown; recurring_id?: unknown }
    return {
      text: typeof message === 'string' ? message : '',
      owner: typeof recurring_id === 'string' && recurring_id !== '' ? recurring_id : null,
    }
  }
  return { text: '', owner: null }
}

/**
 * The three 409s `link_payment` raises, told apart by what the backend says.
 * The owner id is the strongest signal; the two string details are matched on
 * the phrase that distinguishes them, and anything else is not classified.
 */
function linkConflict(error: unknown): PeriodLinkError {
  const { text, owner } = detailOf(error)
  if (owner !== null) return new PeriodLinkError('owned', LINK_COPY.owned, owner, error)
  // The owner re-query can come back empty (the race `_already_linked_conflict`
  // documents): still another bill's, just with nowhere to link to.
  if (/another recurring/i.test(text)) return new PeriodLinkError('owned', LINK_COPY.owned, null, error)
  if (/billing period/i.test(text)) return new PeriodLinkError('filled', LINK_COPY.filled, null, error)
  if (/already linked to this recurring/i.test(text)) return new PeriodLinkError('same', LINK_COPY.same, null, error)
  return new PeriodLinkError('failed', LINK_COPY.failed, null, error)
}

const failedCopy = (op: Op): string => (op.op === 'link' ? LINK_COPY.failed : op.op === 'unlink' ? UNLINK_FAILED : FIND_FAILED)

async function send(billId: string, op: Op): Promise<Outcome> {
  const path = { recurring_id: billId }
  try {
    if (op.op === 'link') {
      const { data, error, response } = await api.POST('/api/v1/recurring/{recurring_id}/payments', {
        params: { path },
        // The PERIOD's due date: the server snaps it onto the grid and files
        // the transaction there. Never `nextDue` — that is Mark paid's period.
        body: { transaction_id: op.transactionId, due_date: op.dueDate },
      })
      if (error === undefined && data !== undefined) return { op: 'link', filed: toFiledPayment(data) }
      if (response.status === 409) throw linkConflict(error)
      throw new PeriodLinkError('failed', LINK_COPY.failed, null, error)
    }
    if (op.op === 'unlink') {
      // A cash payment takes the same call: the server deletes it outright.
      const { error } = await api.DELETE('/api/v1/recurring/{recurring_id}/payments/{payment_id}', {
        params: { path: { ...path, payment_id: op.paymentId } },
      })
      if (error === undefined) return { op: 'unlink' }
      throw new PeriodLinkError('failed', UNLINK_FAILED, null, error)
    }
    const { data, error, response } = await api.POST('/api/v1/recurring/{recurring_id}/match-history', { params: { path } })
    if (error === undefined && data !== undefined) return { op: 'find', linked: data.linked }
    if (response.status === 422) throw new PeriodLinkError('unavailable', FIND_UNAVAILABLE, null, error)
    throw new PeriodLinkError('failed', FIND_FAILED, null, error)
  } catch (e) {
    if (e instanceof PeriodLinkError) throw e
    throw new PeriodLinkError('failed', failedCopy(op), null, e)
  }
}

/** Did this write move a transaction onto or off the bill? Then its stream id changed. */
const movesTransaction = (op: Op): boolean => op.op !== 'unlink' || op.transactionId !== null

/**
 * D5, iOS Q17: link a transaction to one period, unlink a period's payment,
 * and "Find past payments" (`match-history`).
 *
 * **One lock per bill, in the mutation cache** (`keys.recurring.links`), the
 * shape `useLifecycle` uses: a sheet that unmounts mid-write and remounts still
 * sees it, and a second write while one is out is refused (`busy`) with
 * nothing sent — each of the three moves which transaction settles which
 * period, so a queued second one would act on a timeline the user has not seen.
 *
 * **Nothing is optimistic.** Which period a link lands in is the server's call
 * (`snap_to_grid`), so the caller gets back the period it was filed under and
 * the settle refetch shows the rest. On settle — success or failure, since a
 * 409 means the server's picture differs from ours — the whole `['recurring']`
 * root is invalidated (detail, timeline, list, every month), plus the
 * transaction reads whenever a transaction's stream id changed: every link and
 * every search, and every unlink except a cash one.
 *
 * Every function rejects with a `PeriodLinkError` whose `message` is the
 * sentence to show; nothing here toasts — the sheet and the detail show each
 * outcome where the user is looking.
 */
export function usePeriodLinks(bill: Bill): {
  link: (transactionId: string, dueDate: LocalDate) => Promise<Filed>
  unlink: (payment: Pick<Payment, 'id' | 'transactionId'>) => Promise<void>
  findPast: () => Promise<number>
  busy: boolean
} {
  const client = useQueryClient()
  const mutationKey = keys.recurring.links(bill.id)
  const busy = useMutationState({ filters: { mutationKey, exact: true, status: 'pending' } }).length > 0

  const { mutateAsync } = useMutation<Outcome, PeriodLinkError, Op>({
    mutationKey,
    mutationFn: (op) => send(bill.id, op),
    onSuccess: (out) => emitLinkOutcome(out),
    // Not awaited: the caller settles on the server's answer, and a slow
    // refetch must not hold the lock.
    onSettled: (_data, _error, op) => {
      void client.invalidateQueries({ queryKey: keys.recurring.all() })
      if (movesTransaction(op)) void client.invalidateQueries({ queryKey: keys.transactions.all() })
    },
  })

  const run = useCallback(
    async (op: Op): Promise<Outcome> => {
      const pending = client.getMutationCache().findAll({ mutationKey: keys.recurring.links(bill.id), exact: true, status: 'pending' })
      if (pending.length > 0) throw new PeriodLinkError('busy', BUSY_COPY)
      return mutateAsync(op)
    },
    [client, bill.id, mutateAsync],
  )

  return {
    link: useCallback(
      async (transactionId: string, dueDate: LocalDate) => {
        const out = await run({ op: 'link', transactionId, dueDate })
        if (out.op !== 'link') throw new PeriodLinkError('failed', LINK_COPY.failed)
        return out.filed
      },
      [run],
    ),
    unlink: useCallback(
      async (payment: Pick<Payment, 'id' | 'transactionId'>) => {
        await run({ op: 'unlink', paymentId: payment.id, transactionId: payment.transactionId })
      },
      [run],
    ),
    findPast: useCallback(async () => {
      const out = await run({ op: 'find' })
      return out.op === 'find' ? out.linked : 0
    }, [run]),
    busy,
  }
}
